import { useContext, useEffect } from 'react'
import { unique, uniqueBy } from 'lib/array/arrayUtils'
import { fetchExperiencesById, fetchExperienceTimes } from 'actions/ExperienceActions'
import { getExperienceTimesKey } from 'lib/experiences/experienceUtils'
import { useAppDispatch } from 'hooks/reduxHooks'
import { isExperienceItem, isTransferItem } from 'lib/checkout/checkoutUtils'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import GeoContext from 'contexts/geoContext'

/**
 * XState-compatible version of useFetchExperienceItemData
 * Fetches experience data for multi-cart items from XState context
 */
function useFetchMultiCartExperienceItemData() {
  const dispatch = useAppDispatch()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const { currentCurrency } = useContext(GeoContext)

  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartItemsState = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)

  const hasMultiCartItems = cartItemsState.status === 'loaded' && activeCartId && Object.keys(cartItemsState.data[activeCartId] || {}).length > 0

  // Extract experience and transfer items from XState multi-cart
  const { experienceItems, transferItems } = useSelector(multiCartActorRef, (snapshot) => {
    if (snapshot.context.cartItems.status !== 'loaded' || !activeCartId) {
      return { experienceItems: [], transferItems: [] }
    }

    const cartItems = Object.values(snapshot.context.cartItems.data[activeCartId] || {})
    const activeCartItems = cartItems
      .filter(multiCartItem => multiCartItem.state !== 'deleted') // Filter out deleted items
      .map(multiCartItem => multiCartItem.item)

    return {
      experienceItems: activeCartItems.filter(isExperienceItem),
      transferItems: activeCartItems.filter(isTransferItem),
    }
  })

  useEffect(() => {
    if (!hasMultiCartItems) return // Skip if no multi-cart items

    const experienceFetchItems = experienceItems.filter(item => item.offerType !== 'addon')

    // Fetch experiences if needed
    const experienceIds = unique(experienceFetchItems.map(item => item.experienceId))
    if (experienceIds.length > 0) {
      dispatch(fetchExperiencesById(...experienceIds))
    }

    // Get the ticket data for each combination of experience, date, and pickup point
    // so we can retrieve the tickets
    const itemsToFetchTimesFor = uniqueBy(experienceFetchItems, (item) =>
      getExperienceTimesKey(item.experienceId, item.date, {
        currency: currentCurrency,
        pickupPointId: item.pickupPointId,
        redemptionLocationId: item.redemptionLocationId,
        ticketModesMap: item.ticketModesMap,
      }),
    )

    const experienceTimesParams = itemsToFetchTimesFor.map(
      (item: App.Checkout.ExperienceItem): Parameters<typeof fetchExperienceTimes> => [
        item.experienceId,
        {
          date: item.date,
          currency: currentCurrency,
          pickupPointId: item.pickupPointId,
          redemptionLocationId: item.redemptionLocationId,
          isBuyNowBookLater: item.isBuyNowBookLater,
          ticketModesMap: item.ticketModesMap,
          isGift: item.isGift,
        },
      ],
    )

    for (const params of experienceTimesParams) {
      dispatch(fetchExperienceTimes(...params))
    }
  }, [currentCurrency, dispatch, experienceItems, hasMultiCartItems])

  // Fetch transfer experience data
  useEffect(() => {
    if (!hasMultiCartItems) return // Skip if no multi-cart items

    const transferExperienceIds = transferItems.map(transfer => transfer.experienceId)
    if (transferExperienceIds.length > 0) {
      dispatch(fetchExperiencesById(...transferExperienceIds))
    }
  }, [dispatch, transferItems, hasMultiCartItems])
}

export default useFetchMultiCartExperienceItemData
